(ns storrito.instagram.story.editor.render
  (:require [storrito.instagram.story.editor.dimension :as dimension]
            [storrito.image :as image]
            [cljs.core.async :refer [<!] :as async]
            [sv.core.async :refer-macros [<?]])
  (:require-macros [cljs.core.async.macros :refer [go]]))

(defn to-data-url
  "Invokes `toDataURL` on the Fabricjs canvas object and takes care
   that the params are converted to a JS object before."
  [fabricjs-params fabricjs-canvas]
  (->> (clj->js fabricjs-params)
       (.toDataURL fabricjs-canvas)))

(defn crop-story-image-params
  "Returns parameters to invoke `toDataURL` on Fabricjs canvas object
   that crops the story image inside the mask (see also
   http://fabricjs.com/docs/fabric.Canvas.html#toDataURL for further
   parameters)."
  ;; TODO: consider non-supported transparency in JPG at the moment
  ;;       transparent parts will be black.
  [dimensions display-zoom-factor]
  { ;; JPG is used here, since a 1080x1920 PNG with around 5mb pretty
   ;; huge to be uploaded via a slow internet connection. A comparable
   ;; JPG is around 180 KB.
   :format "jpeg"
   :quality 0.9 ;; JPEG quality
   :left       (:margin/left dimensions)
   :top        (:margin/top dimensions)
   :width      (:image/width dimensions)
   :height     (:image/height dimensions)
   :multiplier (/ 1 display-zoom-factor)})

(defn ^:private render [fabricjs-canvas
                        model
                        data-extraction-fn
                        options]
  (let [retina-scaling-enabled (.-enableRetinaScaling fabricjs-canvas)
        zoom-factor (:dimension/zoom model)]
    ;; on a Retina device the `(.-devicePixelRatio js/window)` is
    ;; greater than 1. A devicePixelRatio of 2 would render images
    ;; with the size doubled. Therefore the retina scaling is turned
    ;; off here, while the image is rendered to a data url:
    (set! (.-enableRetinaScaling fabricjs-canvas) false)

    ;; The overlay image is only used in the editor to help the users to see the
    ;; boundaries of the story canvas and where the Instagram Stories UI
    ;; controls will appear. However, these should not be part of the preview
    ;; picture:
    (.setOverlayImage fabricjs-canvas
                      nil)
    (try
      (-> (dimension/for-model model)
          ;; We need to scale down our margins etc. to get the correct
          ;; margins. To get a large render, we pass `zoom-factor` to
          ;; `crop-story-image-params` which inverses it and passes it
          ;; as `multiplier` to FabricJS
          (dimension/scale zoom-factor)
          (crop-story-image-params zoom-factor)
          (merge options)
          (data-extraction-fn fabricjs-canvas))
      (finally
        (set! (.-enableRetinaScaling fabricjs-canvas) retina-scaling-enabled)))))

(defn render-to-data-url
  "Renders and crops the content on the `fabricjs-canvas` based on the
   information in the given `model-value` of the story image editor."
  ([fabricjs-canvas model-value]
   (render-to-data-url fabricjs-canvas model-value {}))
  ([fabricjs-canvas model-value options]
   (render fabricjs-canvas model-value
           (fn [fabricjs-canvas fabricjs-params]
             (to-data-url fabricjs-canvas fabricjs-params))
           options)))

(defn data-uri->blob [data-uri]
  (let [c (async/promise-chan)
        req (js/XMLHttpRequest.)]
    (.open req "GET" data-uri)
    (set! req.responseType "arraybuffer")
    (->> (fn [_e]
           (let [mime (.getResponseHeader req "content-type")]
             (->> (js/Blob. #js[req.response] #js{:type mime})
                  (async/put! c))))
         (.addEventListener req "load"))
    (.send req)
    c))

(def preview-quality 0.7)
(def preview-mime-type "image/jpeg")

(defn canvas->preview-blob [canvas]
  (image/canvas-blob canvas {:mime-type preview-mime-type
                             :quality preview-quality}))

(defn canvas->preview-data-uri [canvas]
  (go (.toDataURL canvas preview-mime-type preview-quality)))

(defn scale-image [{:keys [width height]} image f]
  (go
    (let [canvas (js/document.createElement "canvas")]
      (set! (.-width canvas)  width)
      (set! (.-height canvas) height)
      (doto (.getContext canvas "2d")
        (.drawImage image 0 0 width height))
      (<! (f canvas)))))

(defn constrain-image!
  [max-side-length blob-or-uri]
  (go
    (let [blob? (instance? js/Blob blob-or-uri)
          image (<? (image/load-image blob-or-uri))
          image-width (.-width image)
          image-height (.-height image)
          scaling-factor (min 1.0 (/ max-side-length (max image-width image-height)))
          width (* scaling-factor image-width)
          height (* scaling-factor image-height)]
      (<? (scale-image {:width width :height height}
                       image
                       (if blob?
                         canvas->preview-blob
                         canvas->preview-data-uri))))))

(defn constrain-image-file!
  [file {:keys [max-side-length quality]}]
  (go
    (let [image (<? (image/load-image file))
          image-width (.-width image)
          image-height (.-height image)
          scaling-factor (min 1.0 (/ max-side-length (max image-width image-height)))
          width (* scaling-factor image-width)
          height (* scaling-factor image-height)]
      (if (< scaling-factor 1.0)
        (let [blob (<! (scale-image {:width width :height height}
                                    image
                                    (fn [canvas]
                                      (image/canvas-blob canvas
                                                         {:mime-type (.-type file)
                                                          :quality quality}))))]
          (js/File. [blob] (.-name file)
                    (clj->js {:type (.-type file)})))
        file))))
